// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { Skin } from '../skin';
import { classicSkin } from './classic';

/**
 * Yardage Book: the paper book a caddie carries. Warm grey-beige paper, olive ink, moss green
 * as the accent, a serif headline face set large. Light on dark is inverted throughout, so
 * every "text on accent" and "text on light card" pair is re-picked rather than copied.
 * The hole map keeps classic's drawing fills, which already come from this world.
 */
export const yardageBookSkin: Skin = {
  colors: {
    background: {
      app: '#EFEBE1',
      panel: '#F8F5EE',
      panelAlt: '#F2EEE4',
      elevated: '#E6E0D2',
      deep: '#E4DECF',
      interactive: '#4F6A30',
      interactiveMuted: '#C9CFB8',
    },
    surface: {
      base: '#F8F5EE',
      alt: '#F2EEE4',
      elevated: '#E6E0D2',
      input: '#FBF9F4',
      muted: '#E1DAC9',
      card: '#F4F0E6',
      light: '#FBFAF6',
      toggleOff: '#B9B5A6',
      tint: '#D6E0C4',
      tintMid: '#C7D4B1',
      tintDeep: '#B8C79E',
      brown: '#D9C8A8',
      brownLight: '#E8DCC3',
      brownDeep: '#C7B48E',
      brownDark: '#B39F75',
    },
    text: {
      primary: '#2F3A26',
      secondary: '#5B6650',
      muted: '#6F7863',
      heading: '#2F3A26',
      inverse: '#FBFAF4',
      hint: '#636C58',
      subtle: '#6E7862',
      faint: '#8A927E',
      dim: '#9AA28F',
      accentMuted: '#4F6A30',
      // Dark enough to read on surface.tintMid, this skin's mid sage: the old #4E6B2F was 3.89:1.
      accentSoft: '#456026',
      positive: '#3E6B2B',
      warm: '#7A5B2A',
      onLight: '#2F3A26',
      slateMuted: '#6F7C88',
      // Ink that flips with the skin, by role: placeholder text, a label on a strong fill.
      placeholder: '#8A927E',
      onStrong: '#FBFAF4',
      // Ink over photos, video and the map: white in every skin, on purpose.
      onMedia: '#FFFFFF',
    },
    border: {
      default: 'rgba(47,58,38,0.12)',
      strong: '#B9AE95',
      accent: '#4F6A30',
      accentMuted: '#C9CFB8',
      onLight: '#D8D0BE',
      brown: '#A67C2E',
      // Low-contrast rules on panels; they flip with the skin, unlike a white at low alpha.
      hairline: 'rgba(47,58,38,0.14)',
      subtle: 'rgba(47,58,38,0.25)',
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      slate: '#E2E8F0',
      navy: '#111827',
      navyDeep: '#0C1435',
      ink: '#040711',
      grey: '#6B7280',
      greyDark: '#5B5B5B',
      charcoal: '#2C2C2C',
    },
    accent: {
      primary: '#4F6A30',
      positive: '#4E6B2F',
      secondary: '#7A9452',
      light: '#A7BE86',
      soft: '#8FA96C',
      lime: '#8FA53A',
      teal: '#3F7A74',
      tealDeep: '#2A5551',
      indigo: '#4C5A7E',
      glow: '#6B8A47',
      subtleBg: '#E9EBDD',
      subtleBorder: 'rgba(79,106,48,0.3)',
      overlay: 'rgba(47,58,38,0.45)',
      gold: '#8C6520',
      goldDeep: '#75521A',
      goldMuted: '#9E7A3E',
      goldSoft: 'rgba(166,124,46,0.16)',
      goldBorder: 'rgba(166,124,46,0.45)',
      freeze: '#5C8FB8',
      freezeMuted: 'rgba(92,143,184,0.55)',
      blue: '#3E74B8',
      blueSoft: '#6F9BD1',
      blueDeep: '#244E8C',
      blueMuted: '#4A6E9C',
      mint: '#2E9C77',
      mintDeep: '#1F7A5C',
      mintSoft: '#7FC9AC',
      limeSoft: '#C9D98E',
      glowBright: '#7FA05A',
      olive: '#8E8F4A',
      bronze: '#A86E3F',
      orange: '#C8641E',
      purple: '#6A5A8A',
    },
    chart: {
      pink: '#C96A9C',
      peach: '#D07A5A',
      salmon: '#C4836E',
      sand: '#C9A64A',
      sky: '#5F8FC0',
    },
    status: {
      danger: '#A23E32',
      dangerStrong: '#8A3128',
      dangerText: '#8F3A30',
      dangerTextSoft: '#A65A50',
      dangerBg: '#F0D9D4',
      dangerBgMuted: '#E9CFC8',
      errorText: '#A23E32',
      errorTextStrong: '#8F2F24',
      errorTextSoft: '#B8665C',
      warning: '#8C5A12',
      warningText: '#7A4F0E',
      warningBorder: '#C48A2E',
      warningBg: '#F2E4C4',
      success: '#4E6B2F',
      successBg: '#E1E8CF',
      successStrong: '#3E7A2E',
      dangerVivid: '#C0392B',
      dangerBgStrong: '#E6C2BC',
      dangerPanel: '#F3E1DD',
      errorTextMuted: '#B27068',
      warningStrong: '#B26A10',
      warningBorderMuted: '#C9A66B',
    },
    button: {
      primaryBg: '#4F6A30',
      primaryBorder: '#4F6A30',
      primaryText: '#FBFAF4',
      secondaryBg: '#F2EEE4',
      secondaryBorder: '#B9AE95',
      secondaryText: '#2F3A26',
      ghostBg: 'transparent',
      ghostBorder: '#B9AE95',
      ghostText: '#5B6650',
    },
  },
  fonts: {
    brand: 'Fraunces',
    mono: 'SpaceMono',
    webSerif: 'Fraunces, Georgia, "Times New Roman", serif',
    systemSerif: 'serif',
    systemMono: 'monospace',
  },
  // Plain buttons, as on paper forms: a modest corner, a sentence-case label at reading size.
  button: {
    radius: 10,
    borderWidth: 1,
    label: { fontSize: 16, fontWeight: '600', letterSpacing: 0.2, textTransform: 'none' },
  },
  // No wooden planks and no cartoon animals on paper: the score input draws its buttons,
  // its par marker and its hazard glyphs from this palette instead of the painted assets.
  chrome: {
    scoreInput: 'drawn',
  },
  map: classicSkin.map,
};
